import React from "react";
import styled from "styled-components";
import colors from "@styling/colors";
import { spacing, borderRadius } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import type { RestStore } from "@KassaroAB/shared/types/store";
import { DropdownArrow } from "../../components/Navbar";
import { useTranslation } from "react-i18next";

const StoreDropdownContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const StoreDropdownTrigger = styled.button`
  display: flex;
  align-items: center;
  gap: ${spacing.x1}px;
  padding: ${spacing.x1}px ${spacing.x2}px;
  border: none;
  border-radius: ${borderRadius.small}px;
  background-color: ${colors.backgroundDark};
  color: ${colors.text};
  font-size: ${fontSizes.small}px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 120px;
  justify-content: space-between;

  &:hover {
    background-color: ${colors.backgroundDark};
    opacity: 0.9;
    color: ${colors.primary};
  }
`;

const StoreDropdownMenu = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: calc(100% + ${spacing.x1}px);
  left: 0;
  min-width: 180px;
  max-width: 300px;
  max-height: 300px;
  overflow-y: auto;
  background-color: ${colors.background};
  border: none;
  border-radius: ${borderRadius.regular}px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  visibility: ${({ $isOpen }) => ($isOpen ? "visible" : "hidden")};
  transform: ${({ $isOpen }) => ($isOpen ? "translateY(0)" : "translateY(-8px)")};
  transition: all 0.2s;
  z-index: 1000;
  overflow: hidden;
`;

const StoreDropdownItem = styled.button<{ $isSelected?: boolean }>`
  width: 100%;
  padding: ${spacing.x2}px ${spacing.x3}px;
  border: none;
  background: none;
  color: ${({ $isSelected }) => ($isSelected ? colors.primary : colors.text)};
  font-size: ${fontSizes.small}px;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s;
  font-weight: ${({ $isSelected }) => ($isSelected ? 600 : 400)};

  &:hover {
    background-color: ${colors.backgroundDark};
  }
`;

interface StoreDropdownProps {
  stores: RestStore[];
  selectedStoreId: string | null;
  onStoreChange: (storeId: string) => void;
}

export const StoreDropdown = ({ stores, selectedStoreId, onStoreChange }: StoreDropdownProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = React.useState(false);
  const selectedStore = stores.find((store) => store.id === selectedStoreId);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('[data-store-dropdown]')) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  const handleStoreSelect = (storeId: string) => {
    onStoreChange(storeId);
    setIsOpen(false);
  };

  return (
    <StoreDropdownContainer data-store-dropdown>
      <StoreDropdownTrigger onClick={() => setIsOpen(!isOpen)}>
        <span>{selectedStore?.name || t("storeDropdown.selectStore")}</span>
        <DropdownArrow $isOpen={isOpen} />
      </StoreDropdownTrigger>
      <StoreDropdownMenu $isOpen={isOpen}>
        {stores.map((store) => (
          <StoreDropdownItem
            key={store.id}
            $isSelected={store.id === selectedStoreId}
            onClick={() => handleStoreSelect(store.id)}
          >
            {store.name}
          </StoreDropdownItem>
        ))}
      </StoreDropdownMenu>
    </StoreDropdownContainer>
  );
};
